/**
 * ============================================================================
 * WEBSOCKET CONTEXT - COMUNICAÇÃO EM TEMPO REAL
 * ============================================================================
 *
 * Este contexto é responsável por:
 * - Gerenciar conexões WebSocket
 * - Sincronizar com Redux para estado global
 * - Fornecer sendMessage para componentes filhos
 * - Tratar reconexão automática quando sessionId muda
 *
 * PONTOS CRÍTICOS:
 * - WebSocketProvider DEVE envolver componentes que usam useWebSocket
 * - sessionId é usado para construir URL de conexão
 * - Token JWT é obtido do localStorage
 * - Reconexão automática quando sessionId muda
 * - Logs detalhados para debugging
 *
 * FLUXO DE CONEXÃO:
 * 1. sessionId muda → fecha conexão antiga
 * 2. Cria nova conexão com token JWT
 * 3. Atualiza Redux com status de conexão
 * 4. Fornece sendMessage para componentes filhos
 * ============================================================================
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  setConnected,
  setDisconnected,
  setError,
  setReconnecting,
  addMessage,
} from "@/store/slices/websocketSlice";
import { websocketConfig } from "@/config/websocket";
import { logger } from "@/utils/logger";
import { useAuth } from "./AuthContext";
import { useSelector, useDispatch } from 'react-redux';
import { store } from "@/store";

/**
 * Interface do contexto WebSocket
 *
 * Propriedades:
 * - isConnected: Status da conexão WebSocket
 * - sendMessage: Função para enviar mensagens
 * - error: Erro da conexão (se houver)
 * - isReconnecting: Status de reconexão
 */
interface WebSocketContextType {
  isConnected: boolean;
  sendMessage: (message: string) => void;
  error: string | null;
  isReconnecting: boolean;
}

/**
 * Contexto WebSocket
 * CRÍTICO: Deve ser usado apenas dentro do WebSocketProvider
 */
const WebSocketContext = createContext<WebSocketContextType | undefined>(
  undefined,
);

/**
 * Props do WebSocketProvider
 *
 * Propriedades:
 * - children: Componentes filhos que usarão o contexto
 * - sessionId: ID da sessão para conectar ao WebSocket
 */
interface WebSocketProviderProps {
  children: React.ReactNode;
  sessionId: string | null;
}

/**
 * ============================================================================
 * WEBSOCKETPROVIDER - COMPONENTE PRINCIPAL DO CONTEXTO
 * ============================================================================
 *
 * Responsabilidades:
 * - Gerenciar ciclo de vida da conexão WebSocket
 * - Sincronizar estado com Redux
 * - Fornecer contexto para componentes filhos
 * - Tratar reconexão automática
 *
 * PONTOS CRÍTICOS:
 * - wsRef mantém referência da conexão WebSocket
 * - useEffect principal gerencia conexão baseada no sessionId
 * - Reconexão automática quando sessionId muda
 * - Logs detalhados para debugging
 * ============================================================================
 */
export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
  sessionId,
}) => {
  const dispatch = useAppDispatch();
  const selectedDocumentId = useSelector((state: any) => state.chat.selectedDocumentId);

  const isMountedRef = useRef(false);
  const wsRef = useRef<WebSocket | null>(null);
  const connectedSessionIdRef = useRef<string | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Adicionar controle de tentativas de reconexão
  const reconnectAttemptsRef = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 5;
  const isReconnectingRef = useRef(false);

  // Usar selector correto para o estado do Redux
  const websocketState = useAppSelector((state) => state.websocket);

  // Garantir que isConnected seja sempre do Redux
  const isConnected = websocketState.isConnected;

  // Usar useAuth no nível do componente
  const { refreshToken } = useAuth();

  // Log apenas na primeira montagem
  useEffect(() => {
    if (!isMountedRef.current) {
      logger.info("[WebSocketProvider] ===== MONTANDO WEBSOCKET PROVIDER =====");
      logger.info(`[WebSocketProvider] 🔍 Parâmetros de inicialização:`, {
        sessionId,
        hasSessionId: !!sessionId,
        sessionIdType: typeof sessionId,
        childrenCount: React.Children.count(children)
      });
      isMountedRef.current = true;
    }
  }, [sessionId, children]);

  // Efeito de conexão simplificado
  useEffect(() => {
    logger.info(`[WebSocketProvider] 🔄 useEffect de conexão executado`);
    logger.info(`[WebSocketProvider] 🔍 Condições:`, {
      sessionId,
      hasSessionId: !!sessionId,
      isMounted: isMountedRef.current,
      isReconnecting: isReconnectingRef.current,
      reconnectAttempts: reconnectAttemptsRef.current,
      currentWsReadyState: wsRef.current?.readyState,
      isConnected: isConnected,
      isTestEnvironment: process.env.NODE_ENV === 'test'
    });

    // Não criar WebSocket em ambiente de teste
    if (process.env.NODE_ENV === 'test') {
      logger.info("[WebSocketProvider] 🧪 Ambiente de teste detectado - não criando WebSocket");
      return;
    }

    // Usar sessionId da prop, não do Redux
    const sessionIdToUse = sessionId;

    if (sessionIdToUse && isMountedRef.current) {
      // Verificar se já está conectado com o mesmo sessionId
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        if (connectedSessionIdRef.current === sessionIdToUse) {
          logger.info("[WebSocketProvider] ✅ Já conectado ao MESMO sessionId - não reconectando");
          return;
        } else {
          logger.info(`[WebSocketProvider] 🔄 SessionId mudou de ${connectedSessionIdRef.current} para ${sessionIdToUse}. Forçando reconexão.`);
          // Fechar a conexão antiga explicitamente para permitir a nova
          wsRef.current.close();
          wsRef.current = null;
        }
      }

      // Verificar se está tentando reconectar muito rapidamente
      if (isReconnectingRef.current) {
        logger.info("[WebSocketProvider] ⏳ Já tentando reconectar - aguardando...");
        return;
      }

      // Resetar contadores ao conectar
      reconnectAttemptsRef.current = 0;
      isReconnectingRef.current = false;

      // VALIDAÇÃO: Verificar se sessionId é válido
      if (sessionIdToUse === "undefined" || sessionIdToUse === undefined) {
        logger.error(`[WebSocketProvider] ❌ SessionId inválido: ${sessionIdToUse}`);
        dispatch(setError(`Session ID inválido: ${sessionIdToUse}`));
        return;
      }

      // Evitar múltiplas tentativas simultâneas
      if (isReconnectingRef.current) {
        logger.warn("[WebSocketProvider] ❌ Já tentando reconectar, ignorando nova tentativa");
        return;
      }

      // Verificar limite de tentativas
      if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
        logger.error(`[WebSocketProvider] ❌ Máximo de tentativas (${MAX_RECONNECT_ATTEMPTS}) atingido. Parando reconexão.`);
        dispatch(setError("Máximo de tentativas de reconexão atingido"));
        return;
      }

      isReconnectingRef.current = true;
      logger.info(`[WebSocketProvider] 🚀 Iniciando processo de conexão...`);

      // Limpar timeout de reconexão anterior
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Fechar conexão anterior se existir
      if (wsRef.current) {
        logger.info("[WebSocketProvider] Cleanup: fechando conexão WebSocket anterior");
        wsRef.current.close();
        wsRef.current = null;
      }

      const token = localStorage.getItem("access_token");
      if (!token) {
        logger.error("[WebSocketProvider] ❌ Token não encontrado no localStorage");
        isReconnectingRef.current = false;
        return;
      }

      // Construir URL com sessionId correto
      const wsUrl = `${websocketConfig.baseUrl}/ws/chat/${sessionIdToUse}/?token=${token}`;

      logger.info(`[WebSocketProvider] 🔗 Conectando ao WebSocket:`, {
        url: wsUrl.replace(token, '[TOKEN_HIDDEN]'),
        sessionId: sessionIdToUse,
        hasToken: !!token
      });

      try {
        // Criar conexão WebSocket
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        // Configurar event handlers
        ws.onopen = () => {
          logger.info("[WebSocketProvider] ✅ WebSocket conectado com sucesso à sessão", sessionIdToUse);
          connectedSessionIdRef.current = sessionIdToUse;
          isReconnectingRef.current = false;
          reconnectAttemptsRef.current = 0;

          logger.info("[WebSocketProvider] 🔄 Executando dispatch(setConnected(true))...");
          try {
            if (process.env.NODE_ENV !== 'test') {
              dispatch(setConnected(true));
              logger.info("[WebSocketProvider] ✅ dispatch(setConnected(true)) executado com sucesso");
            } else {
              logger.info("[WebSocketProvider] 🧪 Ambiente de teste - não alterando estado de conexão");
            }
          } catch (error) {
            logger.error("[WebSocketProvider] ❌ Erro ao executar dispatch:", error);
          }

          // Log do estado após dispatch - CORRIGIDO: usar store.getState() em vez de useAppSelector
          setTimeout(() => {
            const currentState = store.getState();
            logger.info("[WebSocketProvider] 🔍 Estado do Redux após setConnected:", {
              websocketIsConnected: currentState.websocket.isConnected,
              websocketError: currentState.websocket.error,
              websocketIsReconnecting: currentState.websocket.isReconnecting,
              timestamp: new Date().toISOString()
            });
          }, 0);
        };

        // ⭐ CRÍTICO: Handler para receber mensagens do servidor
        ws.onmessage = (event) => {
          try {
            logger.info("[WebSocketProvider] 📨 Mensagem recebida do servidor:", {
              data: event.data,
              timestamp: new Date().toISOString()
            });

            // Tentar fazer parse da mensagem JSON
            let messageData;
            try {
              messageData = JSON.parse(event.data);
              logger.info("[WebSocketProvider] ✅ Mensagem JSON parseada com sucesso:", {
                type: messageData.type,
                id: messageData.id,
                contentLength: messageData.content?.length || 0,
                role: messageData.role || messageData.sender?.type,
                contentPreview: messageData.content?.substring(0, 100) + "..."
              });
            } catch (parseError) {
              logger.warn("[WebSocketProvider] ⚠️ Falha ao fazer parse JSON, tratando como string:", {
                error: parseError,
                data: event.data
              });
              // Se não for JSON, tratar como string simples
              messageData = {
                type: "message",
                content: event.data,
                timestamp: Date.now(),
                role: "assistant",
                id: `ws_${Date.now()}_${Math.random()}`,
                sender: {
                  id: "assistant",
                  type: "assistant",
                  name: "Assistente"
                }
              };
            }

            // Adicionar mensagem ao Redux WebSocket slice
            logger.info("[WebSocketProvider] 🔄 Adicionando mensagem ao Redux...");
            logger.info("[WebSocketProvider] 📤 Payload para addMessage:", {
              id: messageData.id,
              type: messageData.type,
              role: messageData.role,
              contentLength: messageData.content?.length || 0,
              contentPreview: messageData.content?.substring(0, 100) + "..."
            });

            dispatch(addMessage(messageData));
            logger.info("[WebSocketProvider] ✅ Mensagem adicionada ao Redux com sucesso");

          } catch (error) {
            logger.error("[WebSocketProvider] ❌ Erro ao processar mensagem recebida:", error);
          }
        };

        ws.onclose = (event) => {
          logger.info("[WebSocketProvider] 🔌 WebSocket fechado:", event.code, event.reason);
          isReconnectingRef.current = false;
          if (connectedSessionIdRef.current === sessionIdToUse) {
            connectedSessionIdRef.current = null;
          }

          // Sempre definir como desconectado ao fechar
          dispatch(setConnected(false));

          // Lógica de reconexão simplificada para testes
          if (event.code !== 1000 && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
            logger.info(`[WebSocketProvider] 🔄 Tentando reconectar... (${reconnectAttemptsRef.current + 1}/${MAX_RECONNECT_ATTEMPTS})`);
            reconnectAttemptsRef.current++;

            const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
            reconnectTimeoutRef.current = setTimeout(() => {
              if (sessionIdToUse && isMountedRef.current) {
                // A próxima tentativa será executada pelo useEffect
                logger.info("[WebSocketProvider] 🔄 Próxima tentativa de reconexão agendada");
              }
            }, delay);
          }
        };

        ws.onerror = (error) => {
          logger.error("[WebSocketProvider] ❌ Erro no WebSocket:", error);
          isReconnectingRef.current = false;
          dispatch(setError("Erro na conexão WebSocket"));
          dispatch(setConnected(false));
        };

      } catch (error) {
        logger.error("[WebSocketProvider] ❌ Erro ao criar WebSocket:", error);
        isReconnectingRef.current = false;
        dispatch(setError("Falha ao criar conexão WebSocket"));
        dispatch(setConnected(false));
      }
    }
  }, [sessionId, dispatch, isConnected]);

  // Função de envio melhorada
  const sendMessage = useCallback(
    (message: string) => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        logger.warn("[WebSocketProvider] ❌ WebSocket não está conectado, não é possível enviar mensagem");
        return false;
      }

      try {
        // Enviar objeto JSON em vez de string simples
        const messageObject = {
          type: "message",
          content: message,
          timestamp: Date.now(),
          session_id: sessionId,
        };

        const jsonMessage = JSON.stringify(messageObject);
        wsRef.current.send(jsonMessage);
        logger.debug("[WebSocketProvider] ✅ Mensagem enviada:", jsonMessage);
        return true;
      } catch (error) {
        logger.error("[WebSocketProvider] ❌ Erro ao enviar mensagem:", error);
        return false;
      }
    },
    [sessionId]
  );

  const contextValue: WebSocketContextType = useMemo(
    () => {
      logger.info("[WebSocketProvider] 🔄 Criando contextValue:", {
        isConnected,
        error: websocketState.error,
        isReconnecting: isReconnectingRef.current,
        websocketStateIsConnected: websocketState.isConnected,
        wsRefReadyState: wsRef.current?.readyState,
        timestamp: new Date().toISOString()
      });

      const value = {
        isConnected,
        error: websocketState.error,
        sendMessage,
        isReconnecting: isReconnectingRef.current,
      };

      logger.info("[WebSocketProvider] ✅ contextValue criado:", {
        ...value,
        websocketState: {
          isConnected: websocketState.isConnected,
          isReconnecting: websocketState.isReconnecting,
          error: websocketState.error
        }
      });
      return value;
    },
    [isConnected, sendMessage, websocketState.error, websocketState.isConnected, websocketState.isReconnecting]
  );

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

/**
 * ============================================================================
 * USEWEBSOCKET - HOOK PARA USAR O CONTEXTO
 * ============================================================================
 *
 * Responsabilidades:
 * - Fornecer acesso ao contexto WebSocket
 * - Logar chamadas para debugging
 * - Garantir que hook é usado dentro do provider
 *
 * CRÍTICO: Este hook só funciona dentro do WebSocketProvider
 * Se usado fora, retorna undefined
 *
 * Retorna:
 * - isConnected: Status da conexão
 * - sendMessage: Função para enviar mensagens
 */
export const useWebSocket = (): WebSocketContextType | undefined => {
  const context = useContext(WebSocketContext);

  if (!context) {
    logger.error(
      "[useWebSocket] ❌ CONTEXTO NÃO ENCONTRADO - WebSocketProvider não está envolvendo este componente",
    );
  } else {
    logger.info("[useWebSocket] 🔍 Contexto encontrado:", {
      isConnected: context.isConnected,
      error: context.error,
      isReconnecting: context.isReconnecting,
      timestamp: new Date().toISOString()
    });
  }

  return context;
};
